(() => {
  const TAG_NAME = 'sf-health-r3-mechanism';
  if (!window.customElements || customElements.get(TAG_NAME)) return;

  class SfHealthR3Mechanism extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.video = this.querySelector('[data-mechanism-animation]');
      this.stage = this.querySelector('[data-animation-stage]') || this;
      this.toggle = this.querySelector('[data-motion-toggle]');
      this.playIcon = this.querySelector('[data-icon-play]');
      this.pauseIcon = this.querySelector('[data-icon-pause]');
      this.staticNote = this.querySelector('[data-static-note]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      this.autoplay = this.dataset.autoplay !== 'false';
      this.userPaused = !this.autoplay;
      this.visible = false;
      this.nearViewport = false;
      this.sourceAttached = Boolean(this.video?.currentSrc || this.video?.getAttribute('src'));
      this.ready = Boolean(this.video?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA);
      this.mediaError = false;

      this.onToggle = this.onToggle.bind(this);
      this.onLoadedData = this.onLoadedData.bind(this);
      this.onMediaError = this.onMediaError.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPreferenceChange = this.onPreferenceChange.bind(this);

      this.video?.addEventListener('loadeddata', this.onLoadedData);
      this.video?.addEventListener('error', this.onMediaError);
      this.toggle?.addEventListener('click', this.onToggle);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.onPreferenceChange);
      } else {
        this.motionQuery.addListener?.(this.onPreferenceChange);
      }
      this.connection?.addEventListener?.('change', this.onPreferenceChange);

      if (this.ready) this.classList.add('is-animation-ready');
      this.applyPreferences();
      this.observeViewport();
    }

    disconnectedCallback() {
      this.pause();
      this.loadObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.video?.removeEventListener('loadeddata', this.onLoadedData);
      this.video?.removeEventListener('error', this.onMediaError);
      this.toggle?.removeEventListener('click', this.onToggle);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.motionQuery?.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.onPreferenceChange);
      } else {
        this.motionQuery?.removeListener?.(this.onPreferenceChange);
      }
      this.connection?.removeEventListener?.('change', this.onPreferenceChange);
      this.initialized = false;
    }

    get staticMode() {
      return Boolean(this.motionQuery?.matches || this.connection?.saveData);
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) {
        this.nearViewport = true;
        this.visible = true;
        this.maybeAttachSource();
        this.syncPlayback();
        return;
      }

      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.nearViewport = true;
          this.maybeAttachSource();
          this.loadObserver?.disconnect();
        },
        { rootMargin: '360px 0px', threshold: 0.01 },
      );

      this.playbackObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
          this.syncPlayback();
        },
        { threshold: [0, 0.15, 0.5] },
      );

      this.loadObserver.observe(this.stage);
      this.playbackObserver.observe(this.stage);
    }

    maybeAttachSource() {
      if (!this.video || this.sourceAttached || this.staticMode || !this.nearViewport || document.hidden) return;
      const source = this.video.dataset.src;
      if (!source) {
        this.onMediaError();
        return;
      }

      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.preload = 'auto';
      this.video.src = source;
      this.sourceAttached = true;
      this.video.load();
    }

    applyPreferences() {
      const isStatic = this.staticMode;
      this.classList.toggle('is-static', isStatic);
      this.dataset.staticReason = this.motionQuery.matches
        ? 'reduced-motion'
        : this.connection?.saveData
          ? 'save-data'
          : 'none';

      if (this.staticNote) this.staticNote.hidden = !isStatic;
      if (isStatic) {
        this.pause();
      } else {
        this.maybeAttachSource();
      }
      this.syncPlayback();
    }

    onPreferenceChange() {
      this.applyPreferences();
    }

    onVisibilityChange() {
      if (!document.hidden) this.maybeAttachSource();
      this.syncPlayback();
    }

    onLoadedData() {
      this.ready = true;
      this.classList.add('is-animation-ready');
      this.syncPlayback();
    }

    onMediaError() {
      this.mediaError = true;
      this.classList.add('is-animation-error');
      this.pause();
      this.updateToggle();
    }

    onToggle() {
      if (this.staticMode || this.mediaError) return;
      this.userPaused = !this.userPaused;
      if (!this.userPaused) this.maybeAttachSource();
      this.syncPlayback();
    }

    shouldPlay() {
      return Boolean(
        this.video &&
        this.sourceAttached &&
        !this.mediaError &&
        !this.staticMode &&
        !this.userPaused &&
        this.visible &&
        !document.hidden,
      );
    }

    syncPlayback() {
      if (this.shouldPlay()) {
        this.play();
      } else {
        this.pause();
      }
      this.updateToggle();
    }

    async play() {
      if (!this.video || !this.video.paused) return;
      try {
        await this.video.play();
      } catch (_error) {
        if (this.shouldPlay()) this.userPaused = true;
      }
      this.updateToggle();
    }

    pause() {
      this.video?.pause();
    }

    updateToggle() {
      if (!this.toggle) return;
      const unavailable = this.staticMode || this.mediaError || !this.ready;
      this.toggle.hidden = unavailable;
      if (unavailable) return;

      const requestedToPlay = !this.userPaused;
      this.toggle.setAttribute('aria-pressed', String(requestedToPlay));
      this.toggle.setAttribute('aria-label', requestedToPlay ? 'Animation pausieren' : 'Animation abspielen');
      this.playIcon?.toggleAttribute('hidden', requestedToPlay);
      this.pauseIcon?.toggleAttribute('hidden', !requestedToPlay);
    }
  }

  customElements.define(TAG_NAME, SfHealthR3Mechanism);
})();
